"use client";

import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import Link from "next/link";
import Image from "next/image";
import { Icon } from "@repo/ui/icon";
import { gsap } from "../../lib/gsap";
import { useActiveNavigation } from "../../lib/useActiveNavigation";
import { BookNow } from "../book-now";

export default function Header() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const tlRef = useRef<gsap.core.Timeline | null>(null);
  const { getActiveClasses } = useActiveNavigation();

  const close = useCallback(() => setOpen(false), []);
  const openMenu = useCallback(() => setOpen(true), []);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      panelRef.current?.focus();
      return () => {
        document.body.style.overflow = prev;
        triggerRef.current?.focus();
      };
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) close();
  };

  useLayoutEffect(() => {
    const panel = panelRef.current;
    const closeBtn = closeBtnRef.current;
    if (!panel) return;

    const ctx = gsap.context(() => {
      gsap.set(panel, {
        autoAlpha: 0,
        pointerEvents: "none",
        visibility: "hidden",
      });

      if (closeBtn)
        gsap.set(closeBtn, { autoAlpha: 0, scale: 0.95, rotate: -5 });

      const cols = gsap.utils.toArray<HTMLElement>(".menu-col");
      const items = gsap.utils.toArray<HTMLElement>(".menu-col ul li");

      tlRef.current = gsap
        .timeline({
          paused: true,
          defaults: { ease: "power2.out" },
          onReverseComplete: () => {
            panel.style.pointerEvents = "none";
            panel.style.visibility = "hidden";
          },
        })
        .fromTo(
          panel,
          { autoAlpha: 0 },
          { autoAlpha: 1, duration: 0.25, immediateRender: false },
          0,
        )
        .from(
          cols,
          { y: 20, autoAlpha: 0, duration: 0.35, stagger: 0.08 },
          0.05,
        )
        .from(
          items,
          { y: 10, autoAlpha: 0, duration: 0.25, stagger: 0.03 },
          0.15,
        )
        .to(
          closeBtn,
          { autoAlpha: 1, scale: 1, rotate: 0, duration: 0.25 },
          0.12,
        );
    }, panel);

    return () => {
      ctx.revert();
      tlRef.current = null;
    };
  }, []);

  // Play/reverse animation on open change
  useEffect(() => {
    const tl = tlRef.current;
    const panel = panelRef.current;
    if (!tl || !panel) return;

    if (open) {
      // enable interaction BEFORE play to avoid click “glitch”
      panel.style.pointerEvents = "auto";
      panel.style.visibility = "visible";
      tl.play(0);
    } else {
      tl.reverse();
    }
  }, [open]);

  return (
    <>
      <div className="bg-grey-100 h-[79px]" />

      <header className="fixed bg-grey-100 top-0 w-full py-4 z-50 border-b border-grey-400">
        <div className="max-w-7xl flex mx-auto justify-between items-center px-6">
          <Link
            href="/"
            aria-label="Go to homepage"
            className="flex-shrink-0"
            onClick={() => {
              // Scroll to top when logo is clicked
              if (typeof window !== "undefined") {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          >
            <Image
              width={83}
              height={33}
              className="invert"
              src="/assets/lms-logo-invert.svg"
              alt="La maison Suisse Logo"
              sizes="83px"
              priority
            />
          </Link>

          <nav aria-label="Primary">
            <ul className="lg:flex hidden flex-row gap-10 transition-colors pl-32 duration-300">
              <li>
                <a
                  className="hover:underline underline-offset-2"
                  href="/#hotels"
                >
                  Hotels
                </a>
              </li>
              <li>
                <a
                  className="hover:underline underline-offset-2"
                  href="/#our-recommendation"
                >
                  Stay
                </a>
              </li>
              <li>
                <a
                  className="hover:underline underline-offset-2"
                  href="/#dining-destinations"
                >
                  Dining
                </a>
              </li>
              <li>
                <Link
                  className={getActiveClasses(
                    "/about/",
                    "hover:underline underline-offset-2",
                  )}
                  href="/about/"
                >
                  About us
                </Link>
              </li>
            </ul>
          </nav>

          <div className="flex flex-row gap-6 items-center">
            <BookNow variant="main">Book now</BookNow>

            {/* Hamburger */}
            <button
              ref={triggerRef}
              type="button"
              onClick={openMenu}
              aria-controls="main-menu-panel"
              aria-expanded={open}
              aria-label="Open menu"
              className="flex flex-col gap-2 cursor-pointer p-4"
            >
              <span className="w-9 h-0.5 bg-black" />
              <span className="w-5 h-0.5 bg-black ml-auto" />
            </button>
          </div>
        </div>
      </header>

      {/* Full-screen panel — always mounted so GSAP can animate it */}
      <div
        id="main-menu-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
        tabIndex={-1}
        ref={panelRef}
        onClick={onBackdropClick}
        className="fixed md:overflow-y-hidden md:pb-0 pb-12 overflow-y-auto top-0 left-0 w-full h-screen bg-grey-100 z-50 flex flex-col md:grid md:grid-cols-12 opacity-0 invisible pointer-events-none will-change-[opacity,transform]"
      >
        <button
          ref={closeBtnRef}
          className="absolute top-6 right-6 cursor-pointer z-50 md:text-black text-white"
          aria-label="Close menu"
          onClick={() => setOpen(false)}
        >
          <Icon name="x" size={30} />
        </button>

        {/* Column 1 */}
        <div className="menu-col col-span-4 h-full relative">
          <Image
            className="object-cover object-right w-full overflow-hidden h-full absolute -z-20"
            width={450}
            height={1000}
            alt="Menu background"
            src="/assets/6I9A4364.webp"
            priority={open}
          />
          <div className="absolute top-0 left-0 w-full h-full bg-black opacity-60 -z-10" />
          <div className="md:pt-32 md:pb-12 py-8 flex flex-col gap-10 md:px-14 px-8">
            <Link
              href="/"
              aria-label="Go to homepage"
              onClick={() => {
                // Scroll to top when logo is clicked
                if (typeof window !== "undefined") {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
                setOpen(false); // Close mobile menu
              }}
            >
              <Image
                width={95}
                height={40}
                src="/assets/lms-logo-invert.svg"
                alt="La maison Suisse Logo"
                loading="lazy"
                sizes="95px"
              />
            </Link>
            <ul className="text-lg text-white flex flex-col gap-3 md:mt-10">
              <li>
                <Link
                  className="hover:underline underline-offset-2"
                  href="/#hotels"
                  onClick={() => setOpen(false)}
                >
                  Our Hotels
                </Link>
              </li>
              <li>
                <Link
                  className="hover:underline underline-offset-2"
                  href="/#our-recommendation"
                  onClick={() => setOpen(false)}
                >
                  Recommendation
                </Link>
              </li>
              <li>
                <Link
                  className="hover:underline underline-offset-2"
                  href="/#dining-destinations"
                  onClick={() => setOpen(false)}
                >
                  Dining Destinations
                </Link>
              </li>
              <li>
                <Link
                  className="hover:underline underline-offset-2"
                  href="/gallery/"
                  onClick={() => setOpen(false)}
                >
                  Gallery
                </Link>
              </li>
            </ul>
            <ul className="text-lg text-white flex flex-col gap-3">
              <li>
                <Link
                  className={getActiveClasses(
                    "/about/",
                    "hover:underline underline-offset-2",
                  )}
                  href="/about/"
                  onClick={() => setOpen(false)}
                >
                  About us
                </Link>
              </li>
              <li>
                <Link
                  className={getActiveClasses(
                    "/careers/",
                    "hover:underline underline-offset-2",
                  )}
                  href="/careers/"
                  onClick={() => setOpen(false)}
                >
                  Careers
                </Link>
              </li>
              <li>
                <Link
                  className={getActiveClasses(
                    "/contact/",
                    "hover:underline underline-offset-2",
                  )}
                  href="/contact/"
                  onClick={() => setOpen(false)}
                >
                  Contact
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Column 2 */}
        <div className="menu-col col-span-4 h-full relative border-r border-gray-300">
          <div className="md:pt-32 md:pb-12 py-8 flex flex-col gap-6 md:px-14 px-8 md:text-center">
            <div className="flex flex-col gap-3 md:items-center">
              <Image
                width={152}
                height={25}
                src="/assets/dottingen-logo.svg"
                alt="Dottingen Logo"
                loading="lazy"
                sizes="(max-width: 768px) 120px, 152px"
              />
              <span className="flex flex-row gap-1 text-grey-700 text-xs items-center">
                <Icon size={12} name="map-pin" />
                Döttingen, Switzerland
              </span>
            </div>
            <ul className="text-lg text-grey-900 flex flex-col gap-3 md:mt-10">
              <li>
                <a
                  className="hover:underline underline-offset-2 hover:text-black flex items-center md:justify-center justify-start gap-1"
                  href="https://dottingen.lmsgroup.ch/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Discover LMS Döttingen
                  <Icon name="arrow-up-right" />
                </a>
              </li>
              <li>
                <a
                  className="hover:underline underline-offset-2 hover:text-black flex items-center md:justify-center justify-start gap-1"
                  href="https://dottingen.lmsgroup.ch/rooms/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Rooms & Suites
                  <Icon name="arrow-up-right" />
                </a>
              </li>
              <li>
                <a
                  className="hover:underline underline-offset-2 hover:text-black flex items-center md:justify-center justify-start gap-1"
                  href="https://dottingen.lmsgroup.ch/#work-friendly-facilities"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Activities
                  <Icon name="arrow-up-right" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Column 3 */}
        <div className="menu-col col-span-4 h-full relative border-r border-gray-300">
          <div className="md:pt-32 md:pb-12 py-8 flex flex-col gap-6 md:px-14 px-8 md:text-center">
            <div className="flex flex-col gap-3 md:items-center">
              <Image
                width={105}
                height={22}
                src="/assets/littau.svg"
                alt="Lucerne Logo"
                loading="lazy"
                sizes="(max-width: 768px) 80px, 105px"
              />
              <span className="flex flex-row gap-1 text-grey-700 text-xs items-center">
                <Icon size={12} name="map-pin" />
                Lucerne, Switzerland
              </span>
            </div>
            <ul className="text-lg text-grey-900 flex flex-col gap-3 md:mt-10">
              <li>
                <span className="flex items-center md:justify-center justify-start gap-1">
                  Coming Soon
                </span>
              </li>
              {/*<li>*/}
              {/*  <a*/}
              {/*    className="hover:underline underline-offset-2 hover:text-black flex items-center md:justify-center justify-start gap-1"*/}
              {/*    href="https://lucerne.lmsgroup.ch/"*/}
              {/*    target="_blank"*/}
              {/*    rel="noopener noreferrer"*/}
              {/*  >*/}
              {/*    Discover LMS Lucerne*/}
              {/*    <Icon name="arrow-up-right" />*/}
              {/*  </a>*/}
              {/*</li>*/}
              {/*<li>*/}
              {/*  <a*/}
              {/*    className="hover:underline underline-offset-2 hover:text-black flex items-center md:justify-center justify-start gap-1"*/}
              {/*    href="https://lucerne.lmsgroup.ch/rooms/"*/}
              {/*    target="_blank"*/}
              {/*    rel="noopener noreferrer"*/}
              {/*  >*/}
              {/*    Rooms & Suites*/}
              {/*    <Icon name="arrow-up-right" />*/}
              {/*  </a>*/}
              {/*</li>*/}
              {/*<li>*/}
              {/*  <a*/}
              {/*    className="hover:underline underline-offset-2 hover:text-black flex items-center md:justify-center justify-start gap-1"*/}
              {/*    href="https://lucerne.lmsgroup.ch/#work-friendly-facilities"*/}
              {/*    target="_blank"*/}
              {/*    rel="noopener noreferrer"*/}
              {/*  >*/}
              {/*    Activities*/}
              {/*    <Icon name="arrow-up-right" />*/}
              {/*  </a>*/}
              {/*</li>*/}
              {/*<li>*/}
              {/*  <a*/}
              {/*    className="hover:underline underline-offset-2 hover:text-black flex items-center md:justify-center justify-start gap-1"*/}
              {/*    href="https://lucerne.lmsgroup.ch/#elevated-experiences-at-lucerne"*/}
              {/*    target="_blank"*/}
              {/*    rel="noopener noreferrer"*/}
              {/*  >*/}
              {/*    Highlights*/}
              {/*    <Icon name="arrow-up-right" />*/}
              {/*  </a>*/}
              {/*</li>*/}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}
